import { useCallback, useEffect, useState } from 'react'
import useAxiosPrivate from './useAxiosPrivate'
import { useAuthContext } from '../context/auth'
import { disablePush, pushSupported, subscribePush } from '../utils/push'

const usePushNotifications = () => {
  const api = useAxiosPrivate()
  const { auth } = useAuthContext()

  const supported = pushSupported()
  const hasToken = Boolean(auth?.accessToken)
  const userId = auth?._id ? String(auth._id) : ''

  const [permission, setPermission] = useState(supported ? Notification.permission : 'unsupported')
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)

  // Already allowed earlier: keep this browser linked to the account that is logged in now
  useEffect(() => {
    if (!supported || !hasToken || !userId) {
      setSubscribed(false)
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        if (Notification.permission === 'granted') {
          await subscribePush(api)
          if (!cancelled) setSubscribed(true)
        } else if (!cancelled) {
          setSubscribed(false)
        }
      } catch (err) {
        console.error('Push sync failed', err)
      }
    })()

    return () => { cancelled = true }
  }, [supported, hasToken, userId, api])

  // Must run from a click: browsers ignore permission requests that come from nowhere
  const enable = useCallback(async () => {
    if (!supported) return
    setBusy(true)
    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result === 'granted') {
        await subscribePush(api)
        setSubscribed(true)
      }
    } catch (err) {
      console.error('Unable to turn on notifications', err)
    } finally {
      setBusy(false)
    }
  }, [supported, api])

  const disable = useCallback(async () => {
    setBusy(true)
    try {
      await disablePush(api)
      setSubscribed(false)
    } catch (err) {
      console.error('Unable to turn off notifications', err)
    } finally {
      setBusy(false)
    }
  }, [api])

  return { supported, permission, subscribed, busy, enable, disable }
}

export default usePushNotifications